import type { ExtUser } from "@src/services/authorizationService";
import { subscriptions } from "@src/services/subscriptions";
import { /* useContext, */ useEffect, useState /* useRef */ } from "react"; // useContext and useRef unused
import FeatureFlagService from "../services/featureFlagService/FeatureFlagService";
import Version from "../utilities/version";

/**
 * Custom hook to check if a specific feature is enabled.
 * @param {string} componentName The name of the component using this hook, for subscription purposes.
 * @param {string} featureName The name of the feature to check.
 * @returns {boolean} Whether the feature is enabled.
 */
const useFeatureEnabled = (componentName: string, featureName: string) => {
	const [isEnabled, setIsEnabled] = useState(false);
	const [userRoles, setUserRoles] = useState<string[]>([]);

	useEffect(() => {
		subscriptions.subscribeImmediate(
			"User",
			"UseFeatureEnabledHook",
			(user: ExtUser) => {
				const roles = user?.profile?.role
					? Array.isArray(user.profile.role)
						? user.profile.role
						: [user.profile.role]
					: [];

				setUserRoles(roles);
			},
		);

		// Cleanup: Unsubscribe from user changes on component unmount
		return () => {
			subscriptions.unsubscribe("User", "UseFeatureEnabledHook");
		};
	}, []);

	useEffect(() => {
		const applicationId = Version.getApplicationId();
		const updateFeatureStatus = () => {
			const enabled = FeatureFlagService.isFeatureEnabled({
				applicationId,
				featureName,
				userRoles,
			});
			console.log(
				"Checking feature status for",
				componentName,
				featureName,
				userRoles,
				enabled,
			);

			// Only update state if the component is mounted
			setIsEnabled(enabled);
		};

		// Initially check if the feature is enabled
		updateFeatureStatus();

		// Subscribe to feature flag changes for real-time updates
		FeatureFlagService.subscribe(
			applicationId,
			componentName,
			updateFeatureStatus,
		);

		// Cleanup: Unsubscribe from feature flag changes on component unmount
		return () => {
			FeatureFlagService.unsubscribe(applicationId, componentName);
		};
	}, [componentName, featureName, userRoles]); // Re-run the effect if componentName, featureName or userRoles change

	return isEnabled;
};

export default useFeatureEnabled;
